import { useSelector } from 'react-redux';
import { examsSelectors } from '../slices/exams/examsSlice';
import type { RootState } from '../store/store';
import { useIsMcGrawHill } from './mcgrawHill';
import { isPreclinicalExam } from '../utils/examUtils';
import { MCGRAW_HILL_VERTICALS } from './mcgrawHillVerticals';
import {
  MCGRAW_HILL_VERTICAL_SCOPES,
  useMcGrawHillVertical,
  type McGrawHillExamMenuEntry,
} from './mcgrawHillVerticalScope';

interface McGrawHillExamMenu {
  /** Whether the exam menu lists this entry or group. */
  shows: (entry: McGrawHillExamMenuEntry) => boolean;
  /** The menu's line when the vertical has no exams; null otherwise. */
  emptyLabel: string | null;
  /** The menu is Nursing's: NCLEX-RN, then a Courses group. */
  hasDutCourses: boolean;
  /** The pill's label in place of the exam's (Nursing's active course). */
  pillLabel: string | null;
}

const EVERYTHING: McGrawHillExamMenu = {
  shows: () => true,
  emptyLabel: null,
  hasDutCourses: false,
  pillLabel: null,
};

/**
 * mcgraw-hill (ORA-1247): the exam menu (ExamSelectors) narrowed to the
 * member's vertical. Everyone else's menu lists everything, as before. The
 * Nursing vertical's menu (ORA-1263, ORA-1268) lists NCLEX-RN and the course,
 * so a member can step out of the course back to NCLEX-RN.
 */
export const useMcGrawHillExamMenu = (): McGrawHillExamMenu => {
  const isMcGrawHill = useIsMcGrawHill();
  const vertical = useMcGrawHillVertical();
  const { examMenu, hasCoursesMenu } = MCGRAW_HILL_VERTICAL_SCOPES[vertical];
  const hasDutCourses = isMcGrawHill && hasCoursesMenu === true;

  // Under Preclinical, Nursing names the active course or book (Nursing
  // Pharmacology, Katzung), not "Preclinical".
  const courseName = useSelector((state: RootState) =>
    hasDutCourses && isPreclinicalExam(examsSelectors.selectPrimaryExam(state))
      ? examsSelectors.selectActiveCustomExam(state)?.name
      : undefined
  );

  if (!isMcGrawHill) return EVERYTHING;

  if (hasDutCourses) {
    return {
      shows: () => false,
      emptyLabel: null,
      hasDutCourses: true,
      pillLabel: courseName ?? null,
    };
  }

  const title =
    MCGRAW_HILL_VERTICALS.find(({ key }) => key === vertical)?.title ??
    vertical;

  return {
    shows: (entry) => examMenu.includes(entry),
    emptyLabel: examMenu.length === 0 ? `No exams yet for ${title}` : null,
    hasDutCourses: false,
    pillLabel: null,
  };
};
